<template>
  <div class="qualification-container">
    <el-card class="qualification-header-card">
      <div class="header-content">
        <div class="header-left">
          <h2>资质管理</h2>
          <div class="store-info">
            <span class="store-name">{{ storeName }}</span>
            <el-tag v-if="form.status === 3" type="success" size="small">已认证</el-tag>
            <el-tag v-else-if="form.status === 2" type="warning" size="small">审核中</el-tag>
            <el-tag v-else-if="form.status === 4" type="danger" size="small">已驳回</el-tag>
            <el-tag v-else type="info" size="small">待提交</el-tag>
          </div>
          <div v-if="form.status === 4 && form.rejectReason" class="reject-reason">
            <el-alert
                title="驳回原因"
                :description="form.rejectReason"
                type="error"
                :closable="false"
                show-icon
            />
          </div>
        </div>
        <div class="header-right">
          <el-button type="success" @click="submitForAudit" :loading="submitting" :disabled="!storeId || form.status === 2">
            <i class="el-icon-upload"></i>
            {{ form.status === 3 ? '重新提交审核' : '全部保存并提交审核' }}
          </el-button>
          <el-button @click="refreshData" :loading="loading">
            <i class="el-icon-refresh"></i>
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <div class="content-wrapper">
      <!-- 左侧目录导航 -->
      <div class="sidebar-nav">
        <div class="nav-title">资质目录</div>
        <div class="nav-list">
          <div
              v-for="item in navItems"
              :key="item.key"
              class="nav-item"
              :class="{ active: activeNav === item.key }"
              @click="scrollToSection(item.key)"
          >
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
            <span v-if="getSectionStatus(item.key)" class="status-dot" :class="getSectionStatus(item.key)"></span>
          </div>
        </div>
      </div>

      <!-- 右侧内容区域 -->
      <div class="main-content" v-loading="loading">

        <!-- ==================== 1. 营业执照卡片 ==================== -->
        <el-card ref="businessLicenseSection" class="section-card">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="el-icon-document"></i>
              <span>营业执照</span>
              <el-tag v-if="form.businessLicenseImageId" type="success" size="mini">已上传</el-tag>
              <el-tag v-else type="danger" size="mini">未上传</el-tag>
            </div>
            <div class="card-actions">
              <span v-if="savedIds.businessLicense" class="save-status">
                <i class="el-icon-circle-check"></i> 已保存
              </span>
              <el-button
                  type="primary"
                  size="small"
                  @click="saveSection('businessLicense')"
                  :loading="sectionSaving.businessLicense"
              >
                <i class="el-icon-check"></i>
                {{ savedIds.businessLicense ? '更新' : '保存' }}
              </el-button>
            </div>
          </div>
          <div class="card-body">
            <el-form :model="form" :rules="rules" ref="businessLicenseForm" label-width="140px">
              <el-form-item label="营业执照号" prop="businessLicenseNo" required>
                <el-input
                    v-model="form.businessLicenseNo"
                    placeholder="请输入营业执照号/统一社会信用代码"
                    maxlength="100"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="公司名称" prop="companyName" required>
                <el-input
                    v-model="form.companyName"
                    placeholder="请输入公司名称"
                    maxlength="200"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="法定代表人" prop="legalPerson" required>
                <el-input
                    v-model="form.legalPerson"
                    placeholder="请输入法定代表人"
                    maxlength="50"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="注册地址" prop="registeredAddress" required>
                <el-input
                    v-model="form.registeredAddress"
                    type="textarea"
                    :rows="2"
                    placeholder="请输入注册地址"
                    maxlength="500"
                    clearable
                />
              </el-form-item>
              <el-form-item label="经营范围" prop="businessScope">
                <el-input
                    v-model="form.businessScope"
                    type="textarea"
                    :rows="3"
                    placeholder="请输入经营范围"
                    maxlength="1000"
                    clearable
                />
              </el-form-item>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="成立日期" prop="establishDate">
                    <el-date-picker
                        v-model="form.establishDate"
                        type="date"
                        placeholder="选择成立日期"
                        value-format="yyyy-MM-dd"
                        style="width: 100%;"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="营业执照有效期" prop="businessLicenseExpireDate">
                    <el-date-picker
                        v-model="form.businessLicenseExpireDate"
                        type="date"
                        placeholder="选择有效期"
                        value-format="yyyy-MM-dd"
                        :disabled="form.isLongTerm === 1"
                        style="width: 100%;"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="长期有效">
                <el-switch
                    v-model="form.isLongTerm"
                    :active-value="1"
                    :inactive-value="0"
                    active-text="是"
                    inactive-text="否"
                />
              </el-form-item>
              <el-form-item label="营业执照" prop="businessLicenseImageId" required>
                <div class="image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectImage('businessLicense')"
                        :image="businessLicenseImage"
                        :show-delete="!!businessLicenseImage"
                        @delete="removeImage('businessLicense')"
                        :style="{width: '300px', height: '200px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectImage('businessLicense')">
                      <i class="el-icon-picture-outline"></i> {{ businessLicenseImage ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader('营业执照')">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                  <div class="image-tips">
                    <p>• 请上传清晰的营业执照照片</p>
                    <p>• 支持 JPG、PNG、GIF、WebP 格式</p>
                  </div>
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>

        <!-- ==================== 2. 食品经营许可证卡片 ==================== -->
        <el-card ref="foodLicenseSection" class="section-card">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="el-icon-star-on"></i>
              <span>食品经营许可证</span>
              <el-tag v-if="form.foodLicenseImageId" type="success" size="mini">已上传</el-tag>
              <el-tag v-else type="info" size="mini">可选</el-tag>
            </div>
            <div class="card-actions">
              <span v-if="savedIds.foodLicense" class="save-status">
                <i class="el-icon-circle-check"></i> 已保存
              </span>
              <el-button
                  type="primary"
                  size="small"
                  @click="saveSection('foodLicense')"
                  :loading="sectionSaving.foodLicense"
              >
                <i class="el-icon-check"></i>
                {{ savedIds.foodLicense ? '更新' : '保存' }}
              </el-button>
            </div>
          </div>
          <div class="card-body">
            <el-form :model="form" :rules="rules" ref="foodLicenseForm" label-width="140px">
              <el-form-item label="许可证号" prop="foodLicenseNo">
                <el-input
                    v-model="form.foodLicenseNo"
                    placeholder="请输入食品经营许可证号"
                    maxlength="100"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="经营项目" prop="foodBusinessItems">
                <el-input
                    v-model="form.foodBusinessItems"
                    type="textarea"
                    :rows="2"
                    placeholder="请输入经营项目，如：预包装食品销售、热食类食品制售"
                    maxlength="500"
                    clearable
                />
              </el-form-item>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="许可证有效期" prop="foodLicenseExpireDate">
                    <el-date-picker
                        v-model="form.foodLicenseExpireDate"
                        type="date"
                        placeholder="选择有效期"
                        value-format="yyyy-MM-dd"
                        :disabled="form.foodLicenseIsLongTerm === 1"
                        style="width: 100%;"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="长期有效">
                    <el-switch
                        v-model="form.foodLicenseIsLongTerm"
                        :active-value="1"
                        :inactive-value="0"
                        active-text="是"
                        inactive-text="否"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="食品许可证" prop="foodLicenseImageId">
                <div class="image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectImage('foodLicense')"
                        :image="foodLicenseImage"
                        :show-delete="!!foodLicenseImage"
                        @delete="removeImage('foodLicense')"
                        :style="{width: '300px', height: '200px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectImage('foodLicense')">
                      <i class="el-icon-picture-outline"></i> {{ foodLicenseImage ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader('食品许可证')">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>

        <!-- ==================== 3. 药品经营许可证卡片 ==================== -->
        <el-card ref="drugLicenseSection" class="section-card">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="el-icon-medicine"></i>
              <span>药品经营许可证</span>
              <el-tag v-if="form.drugLicenseImageId" type="success" size="mini">已上传</el-tag>
              <el-tag v-else type="info" size="mini">可选</el-tag>
            </div>
            <div class="card-actions">
              <span v-if="savedIds.drugLicense" class="save-status">
                <i class="el-icon-circle-check"></i> 已保存
              </span>
              <el-button
                  type="primary"
                  size="small"
                  @click="saveSection('drugLicense')"
                  :loading="sectionSaving.drugLicense"
              >
                <i class="el-icon-check"></i>
                {{ savedIds.drugLicense ? '更新' : '保存' }}
              </el-button>
            </div>
          </div>
          <div class="card-body">
            <el-form :model="form" :rules="rules" ref="drugLicenseForm" label-width="140px">
              <el-form-item label="许可证号" prop="drugLicenseNo">
                <el-input
                    v-model="form.drugLicenseNo"
                    placeholder="请输入药品经营许可证号"
                    maxlength="100"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="经营范围" prop="drugBusinessScope">
                <el-input
                    v-model="form.drugBusinessScope"
                    type="textarea"
                    :rows="2"
                    placeholder="请输入经营范围"
                    maxlength="500"
                    clearable
                />
              </el-form-item>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="许可证有效期" prop="drugLicenseExpireDate">
                    <el-date-picker
                        v-model="form.drugLicenseExpireDate"
                        type="date"
                        placeholder="选择有效期"
                        value-format="yyyy-MM-dd"
                        :disabled="form.drugLicenseIsLongTerm === 1"
                        style="width: 100%;"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="长期有效">
                    <el-switch
                        v-model="form.drugLicenseIsLongTerm"
                        :active-value="1"
                        :inactive-value="0"
                        active-text="是"
                        inactive-text="否"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="药品许可证" prop="drugLicenseImageId">
                <div class="image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectImage('drugLicense')"
                        :image="drugLicenseImage"
                        :show-delete="!!drugLicenseImage"
                        @delete="removeImage('drugLicense')"
                        :style="{width: '300px', height: '200px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectImage('drugLicense')">
                      <i class="el-icon-picture-outline"></i> {{ drugLicenseImage ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader('药品许可证')">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>

        <!-- ==================== 4. 法人信息卡片 ==================== -->
        <el-card ref="legalPersonSection" class="section-card">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="el-icon-user"></i>
              <span>法人信息</span>
              <el-tag v-if="form.idCardFrontImageId && form.idCardBackImageId" type="success" size="mini">已上传</el-tag>
              <el-tag v-else-if="form.idCardFrontImageId || form.idCardBackImageId" type="warning" size="mini">部分上传</el-tag>
              <el-tag v-else type="info" size="mini">待上传</el-tag>
            </div>
            <div class="card-actions">
              <span v-if="savedIds.legalPerson" class="save-status">
                <i class="el-icon-circle-check"></i> 已保存
              </span>
              <el-button
                  type="primary"
                  size="small"
                  @click="saveSection('legalPerson')"
                  :loading="sectionSaving.legalPerson"
              >
                <i class="el-icon-check"></i>
                {{ savedIds.legalPerson ? '更新' : '保存' }}
              </el-button>
            </div>
          </div>
          <div class="card-body">
            <el-form :model="form" :rules="rules" ref="legalPersonForm" label-width="140px">
              <el-form-item label="法人姓名" prop="legalPersonName" required>
                <el-input
                    v-model="form.legalPersonName"
                    placeholder="请输入法人姓名"
                    maxlength="50"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="法人身份证号" prop="legalPersonIdCard" required>
                <el-input
                    v-model="form.legalPersonIdCard"
                    placeholder="请输入法人身份证号"
                    maxlength="18"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="法人联系电话" prop="legalPersonPhone">
                <el-input
                    v-model="form.legalPersonPhone"
                    placeholder="请输入法人联系电话"
                    maxlength="20"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>
              <el-form-item label="身份证正面" prop="idCardFrontImageId" required>
                <div class="image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectImage('idCardFront')"
                        :image="idCardFrontImage"
                        :show-delete="!!idCardFrontImage"
                        @delete="removeImage('idCardFront')"
                        :style="{width: '200px', height: '130px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectImage('idCardFront')">
                      <i class="el-icon-picture-outline"></i> {{ idCardFrontImage ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader('身份证正面')">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                  <div class="image-tips">请上传身份证正面照片</div>
                </div>
              </el-form-item>
              <el-form-item label="身份证反面" prop="idCardBackImageId" required>
                <div class="image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectImage('idCardBack')"
                        :image="idCardBackImage"
                        :show-delete="!!idCardBackImage"
                        @delete="removeImage('idCardBack')"
                        :style="{width: '200px', height: '130px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectImage('idCardBack')">
                      <i class="el-icon-picture-outline"></i> {{ idCardBackImage ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader('身份证反面')">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                  <div class="image-tips">请上传身份证反面照片</div>
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>

        <!-- ==================== 5. 法人手持照片卡片 ==================== -->
        <el-card ref="legalPersonHandheldSection" class="section-card">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="el-icon-camera"></i>
              <span>法人手持照片</span>
              <el-tag v-if="form.legalPersonHandheldImageId" type="success" size="mini">已上传</el-tag>
              <el-tag v-else type="danger" size="mini">未上传</el-tag>
            </div>
            <div class="card-actions">
              <span v-if="savedIds.legalPersonHandheld" class="save-status">
                <i class="el-icon-circle-check"></i> 已保存
              </span>
              <el-button
                  type="primary"
                  size="small"
                  @click="saveSection('legalPersonHandheld')"
                  :loading="sectionSaving.legalPersonHandheld"
              >
                <i class="el-icon-check"></i>
                {{ savedIds.legalPersonHandheld ? '更新' : '保存' }}
              </el-button>
            </div>
          </div>
          <div class="card-body">
            <el-form :model="form" :rules="rules" ref="legalPersonHandheldForm" label-width="140px">
              <el-form-item label="法人手持身份证" prop="legalPersonHandheldImageId" required>
                <div class="image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectImage('legalPersonHandheld')"
                        :image="legalPersonHandheldImage"
                        :show-delete="!!legalPersonHandheldImage"
                        @delete="removeImage('legalPersonHandheld')"
                        :style="{width: '300px', height: '200px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectImage('legalPersonHandheld')">
                      <i class="el-icon-picture-outline"></i> {{ legalPersonHandheldImage ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader('法人手持照片')">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                  <div class="image-tips">
                    <p>• 请法人手持身份证和当日报纸拍照</p>
                    <p>• 确保身份证信息清晰可见</p>
                    <p>• 照片中法人面部需清晰</p>
                  </div>
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>

        <!-- ==================== 6. 其他资质卡片 ==================== -->
        <el-card ref="otherSection" class="section-card">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="el-icon-files"></i>
              <span>其他资质</span>
              <el-tag v-if="otherFiles.length > 0" type="success" size="mini">{{ otherFiles.length }}个文件</el-tag>
              <el-tag v-else type="info" size="mini">可选</el-tag>
            </div>
            <div class="card-actions">
              <span v-if="savedIds.other" class="save-status">
                <i class="el-icon-circle-check"></i> 已保存
              </span>
              <el-button
                  type="primary"
                  size="small"
                  @click="saveSection('other')"
                  :loading="sectionSaving.other"
              >
                <i class="el-icon-check"></i>
                {{ savedIds.other ? '更新' : '保存' }}
              </el-button>
            </div>
          </div>
          <div class="card-body">
            <el-form :model="form" :rules="rules" ref="otherForm" label-width="140px">
              <el-form-item label="资质说明">
                <el-input
                    v-model="form.otherQualificationDesc"
                    type="textarea"
                    :rows="3"
                    placeholder="请填写其他资质文件的补充说明"
                    maxlength="500"
                    clearable
                />
              </el-form-item>
              <el-form-item label="其他资质文件">
                <div class="other-files-manager">
                  <div class="file-list" v-if="otherFiles.length > 0">
                    <div v-for="(file, index) in otherFiles" :key="index" class="file-item">
                      <image-placeholder
                          @click="previewImage(file)"
                          :image="{ id: file.id, url: file.url }"
                          :show-delete="true"
                          @delete="removeOtherFile(index)"
                          :style="{width: '150px', height: '100px'}"
                      />
                      <div class="file-info">
                        <div class="file-name">{{ file.name || '资质文件' }}</div>
                        <el-input
                            v-model="file.description"
                            size="small"
                            placeholder="文件说明"
                            class="file-desc-input"
                        />
                      </div>
                    </div>
                  </div>
                  <el-button size="small" @click="addOtherFile">
                    <i class="el-icon-plus"></i> 添加其他资质
                  </el-button>
                  <div class="image-tips">
                    <p>• 可上传其他相关资质文件，如卫生许可证、消防合格证、商标注册证等</p>
                    <p>• 支持 JPG、PNG、PDF 格式</p>
                  </div>
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>

      </div>
    </div>

    <!-- 图片选择器 -->
    <image-selector
        v-model="imageSelectorVisible"
        :max-count="1"
        @select="handleImageSelect"
    />

    <!-- 图片上传器 -->
    <image-uploader
        :visible.sync="imageUploader.visible"
        @upload="handleImageUploadComplete"
        :category="imageUploader.category"
        v-if="imageUploader.visible"
    />

    <!-- 图片预览对话框 -->
    <el-dialog
        :visible.sync="previewDialog.visible"
        width="600px"
        append-to-body
    >
      <img :src="previewDialog.url" style="width: 100%;" />
      <span slot="footer">
        <el-button @click="previewDialog.visible = false">关闭</el-button>
        <el-button type="primary" @click="copyImageUrl(previewDialog.url)">复制链接</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api"
import ImageSelector from "@/components/ImageSelector"
import ImageUploader from "@/components/ImageUploader"
import ImagePlaceholder from "@/components/ImagePlaceholder"

export default {
  name: 'Qualification',
  components: {
    ImageSelector,
    ImageUploader,
    ImagePlaceholder
  },
  data() {
    const validateIdCard = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入法人身份证号'))
      } else if (!/(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)) {
        callback(new Error('请输入正确的身份证号码'))
      } else {
        callback()
      }
    }

    const validatePhone = (rule, value, callback) => {
      if (value && !/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的手机号码'))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      submitting: false,
      storeId: null,
      storeName: '',
      activeNav: 'businessLicense',

      savedIds: {
        businessLicense: null,
        foodLicense: null,
        drugLicense: null,
        legalPerson: null,
        legalPersonHandheld: null,
        other: null
      },

      sectionSaving: {
        businessLicense: false,
        foodLicense: false,
        drugLicense: false,
        legalPerson: false,
        legalPersonHandheld: false,
        other: false
      },

      navItems: [
        { key: 'businessLicense', label: '营业执照', icon: 'el-icon-document' },
        { key: 'foodLicense', label: '食品经营许可证', icon: 'el-icon-star-on' },
        { key: 'drugLicense', label: '药品经营许可证', icon: 'el-icon-medicine' },
        { key: 'legalPerson', label: '法人信息', icon: 'el-icon-user' },
        { key: 'legalPersonHandheld', label: '法人手持照片', icon: 'el-icon-camera' },
        { key: 'other', label: '其他资质', icon: 'el-icon-files' }
      ],

      form: {
        businessLicenseNo: '',
        companyName: '',
        legalPerson: '',
        registeredAddress: '',
        businessLicenseImageId: null,
        businessScope: '',
        establishDate: '',
        businessLicenseExpireDate: '',
        isLongTerm: 0,
        foodLicenseNo: '',
        foodBusinessItems: '',
        foodLicenseExpireDate: '',
        foodLicenseImageId: null,
        foodLicenseIsLongTerm: 0,
        drugLicenseNo: '',
        drugBusinessScope: '',
        drugLicenseExpireDate: '',
        drugLicenseImageId: null,
        drugLicenseIsLongTerm: 0,
        legalPersonName: '',
        legalPersonIdCard: '',
        legalPersonPhone: '',
        idCardFrontImageId: null,
        idCardBackImageId: null,
        legalPersonHandheldImageId: null,
        otherQualificationDesc: '',
        status: 1,
        rejectReason: ''
      },

      rules: {
        businessLicenseNo: [
          { required: true, message: '请输入营业执照号', trigger: 'blur' }
        ],
        companyName: [
          { required: true, message: '请输入公司名称', trigger: 'blur' }
        ],
        legalPerson: [
          { required: true, message: '请输入法定代表人', trigger: 'blur' }
        ],
        registeredAddress: [
          { required: true, message: '请输入注册地址', trigger: 'blur' }
        ],
        businessLicenseImageId: [
          { required: true, message: '请上传营业执照', trigger: 'change' }
        ],
        legalPersonName: [
          { required: true, message: '请输入法人姓名', trigger: 'blur' }
        ],
        legalPersonIdCard: [
          { required: true, message: '请输入法人身份证号', trigger: 'blur' },
          { validator: validateIdCard, trigger: 'blur' }
        ],
        legalPersonPhone: [
          { validator: validatePhone, trigger: 'blur' }
        ],
        idCardFrontImageId: [
          { required: true, message: '请上传身份证正面', trigger: 'change' }
        ],
        idCardBackImageId: [
          { required: true, message: '请上传身份证反面', trigger: 'change' }
        ],
        legalPersonHandheldImageId: [
          { required: true, message: '请上传法人手持照片', trigger: 'change' }
        ]
      },

      businessLicenseImage: null,
      foodLicenseImage: null,
      drugLicenseImage: null,
      idCardFrontImage: null,
      idCardBackImage: null,
      legalPersonHandheldImage: null,
      otherFiles: [],

      currentImageType: null,
      imageSelectorVisible: false,
      imageUploader: { visible: false, category: null },
      previewDialog: { visible: false, url: '' }
    }
  },

  mounted() {
    this.initData()
  },

  methods: {
    async initData() {
      this.loading = true
      try {
        const storeId = this.$route.query.id
        if (storeId && storeId !== 'undefined' && storeId !== 'null') {
          this.storeId = storeId
          this.storeName = this.$route.query.name || '我的店铺'
          await this.loadQualificationData()
        } else {
          await this.getCurrentSellerInfo()
        }
      } catch (error) {
        console.error('初始化数据失败:', error)
        this.$message.error('初始化数据失败，请刷新重试')
      } finally {
        this.loading = false
      }
    },

    async getCurrentSellerInfo() {
      try {
        const adminRes = await api.login_getAdminInfo(null, {})
        const adminData = adminRes.data || adminRes

        if (adminData.code === '000' && adminData.data) {
          this.storeId = adminData.data.merchantId || null

          if (!this.storeId) {
            this.$message.error('无法获取店铺ID')
            return
          }

          await this.getMerchantDetail(this.storeId)
        } else {
          this.$message.error('获取店铺信息失败')
        }
      } catch (error) {
        console.error('获取管理员信息异常:', error)
        this.$message.error('获取店铺信息失败，请检查网络')
      }
    },

    async getMerchantDetail(merchantId) {
      try {
        const res = await api.merchant_getMerchantDetail({ id: merchantId })
        const responseData = res.data || res

        if (responseData.code === '000' && responseData.data) {
          this.storeName = responseData.data.name || '我的店铺'
          await this.loadQualificationData()
        } else {
          this.$message.error('获取店铺详情失败')
        }
      } catch (error) {
        console.error('获取店铺详情异常:', error)
        this.$message.error('获取店铺详情失败')
      }
    },

    timestampToDateString(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    async loadQualificationData() {
      if (!this.storeId) {
        return
      }

      try {
        const res = await api.merchant_getQualification({ merchantId: this.storeId })
        const responseData = res.data || res

        if (responseData.code === '000' && responseData.data) {
          const data = responseData.data

          this.form.businessLicenseNo = data.businessLicenseNo || ''
          this.form.companyName = data.companyName || ''
          this.form.legalPerson = data.legalPerson || ''
          this.form.registeredAddress = data.registeredAddress || ''
          this.form.businessLicenseImageId = data.businessLicenseImageId || null
          this.form.businessScope = data.businessScope || ''
          this.form.establishDate = this.timestampToDateString(data.establishDate)
          this.form.businessLicenseExpireDate = this.timestampToDateString(data.businessLicenseExpireDate)
          this.form.isLongTerm = data.isLongTerm === undefined ? 0 : (data.isLongTerm ? 1 : 0)

          this.form.foodLicenseNo = data.foodLicenseNo || ''
          this.form.foodBusinessItems = data.foodBusinessItems || ''
          this.form.foodLicenseExpireDate = this.timestampToDateString(data.foodLicenseExpireDate)
          this.form.foodLicenseImageId = data.foodLicenseImageId || null
          this.form.foodLicenseIsLongTerm = data.foodLicenseIsLongTerm === undefined ? 0 : (data.foodLicenseIsLongTerm ? 1 : 0)

          this.form.drugLicenseNo = data.drugLicenseNo || ''
          this.form.drugBusinessScope = data.drugBusinessScope || ''
          this.form.drugLicenseExpireDate = this.timestampToDateString(data.drugLicenseExpireDate)
          this.form.drugLicenseImageId = data.drugLicenseImageId || null
          this.form.drugLicenseIsLongTerm = data.drugLicenseIsLongTerm === undefined ? 0 : (data.drugLicenseIsLongTerm ? 1 : 0)

          this.form.legalPersonName = data.legalPersonName || ''
          this.form.legalPersonIdCard = data.legalPersonIdCard || ''
          this.form.legalPersonPhone = data.legalPersonPhone || ''
          this.form.idCardFrontImageId = data.idCardFrontImageId || null
          this.form.idCardBackImageId = data.idCardBackImageId || null
          this.form.legalPersonHandheldImageId = data.legalPersonHandheldImageId || null
          this.form.otherQualificationDesc = data.otherQualificationDesc || ''
          this.form.status = data.status || 1
          this.form.rejectReason = data.rejectReason || ''

          if (data.savedIds) {
            this.savedIds = { ...this.savedIds, ...data.savedIds }
          } else if (data.id) {
            const mainId = data.id
            this.savedIds = {
              businessLicense: mainId,
              foodLicense: mainId,
              drugLicense: mainId,
              legalPerson: mainId,
              legalPersonHandheld: mainId,
              other: mainId
            }
          }

          if (data.otherQualificationIds) {
            try {
              const otherIds = typeof data.otherQualificationIds === 'string'
                  ? JSON.parse(data.otherQualificationIds)
                  : data.otherQualificationIds
              this.otherFiles = (otherIds || []).map(id => ({ id, url: '', description: '' }))
              await this.loadOtherFilesUrl()
            } catch (e) {
              console.error('解析其他资质文件ID失败:', e)
              this.otherFiles = []
            }
          }

          await this.loadImages()
        }
      } catch (error) {
        console.error('加载资质数据失败:', error)
      }
    },

    async loadImages() {
      const imageFields = [
        { id: this.form.businessLicenseImageId, setter: (url) => { this.businessLicenseImage = { id: this.form.businessLicenseImageId, url } } },
        { id: this.form.foodLicenseImageId, setter: (url) => { this.foodLicenseImage = { id: this.form.foodLicenseImageId, url } } },
        { id: this.form.drugLicenseImageId, setter: (url) => { this.drugLicenseImage = { id: this.form.drugLicenseImageId, url } } },
        { id: this.form.idCardFrontImageId, setter: (url) => { this.idCardFrontImage = { id: this.form.idCardFrontImageId, url } } },
        { id: this.form.idCardBackImageId, setter: (url) => { this.idCardBackImage = { id: this.form.idCardBackImageId, url } } },
        { id: this.form.legalPersonHandheldImageId, setter: (url) => { this.legalPersonHandheldImage = { id: this.form.legalPersonHandheldImageId, url } } }
      ]

      const promises = []
      for (const field of imageFields) {
        if (field.id) {
          promises.push(
              api.image_getImageById({ id: field.id })
                  .then(res => {
                    const responseData = res.data || res
                    if (responseData.code === '000' && responseData.data) {
                      const url = responseData.data.url || responseData.data.path
                      field.setter(url)
                    }
                  })
                  .catch(error => console.error(`加载图片失败 (ID: ${field.id}):`, error))
          )
        }
      }
      await Promise.all(promises)
    },

    async loadOtherFilesUrl() {
      const promises = []
      for (let i = 0; i < this.otherFiles.length; i++) {
        const file = this.otherFiles[i]
        if (file.id && !file.url) {
          promises.push(
              api.image_getImageById({ id: file.id })
                  .then(res => {
                    const responseData = res.data || res
                    if (responseData.code === '000' && responseData.data) {
                      this.otherFiles[i].url = responseData.data.url || responseData.data.path
                    }
                  })
                  .catch(error => console.error(`加载其他资质图片失败 (ID: ${file.id}):`, error))
          )
        }
      }
      await Promise.all(promises)
    },

    getSectionStatus(key) {
      const statusMap = {
        businessLicense: this.form.businessLicenseImageId ? 'success' : 'danger',
        foodLicense: this.form.foodLicenseImageId ? 'success' : '',
        drugLicense: this.form.drugLicenseImageId ? 'success' : '',
        legalPerson: (this.form.idCardFrontImageId && this.form.idCardBackImageId) ? 'success' : (this.form.idCardFrontImageId || this.form.idCardBackImageId) ? 'warning' : 'danger',
        legalPersonHandheld: this.form.legalPersonHandheldImageId ? 'success' : 'danger',
        other: this.otherFiles.length > 0 ? 'success' : ''
      }
      return statusMap[key] || ''
    },

    scrollToSection(key) {
      this.activeNav = key
      const element = this.$refs[`${key}Section`]
      if (element) {
        element.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    selectImage(type) {
      this.currentImageType = type
      this.imageSelectorVisible = true
    },

    handleImageSelect(images) {
      if (images && images.length > 0) {
        const image = images[0]
        this.setImageByType(this.currentImageType, image)
        this.$message.success('图片选择成功')
      }
      this.currentImageType = null
    },

    setImageByType(type, image) {
      const imageData = { id: image.id, url: image.url || image.path }

      switch (type) {
        case 'businessLicense':
          this.businessLicenseImage = imageData
          this.form.businessLicenseImageId = image.id
          break
        case 'foodLicense':
          this.foodLicenseImage = imageData
          this.form.foodLicenseImageId = image.id
          break
        case 'drugLicense':
          this.drugLicenseImage = imageData
          this.form.drugLicenseImageId = image.id
          break
        case 'idCardFront':
          this.idCardFrontImage = imageData
          this.form.idCardFrontImageId = image.id
          break
        case 'idCardBack':
          this.idCardBackImage = imageData
          this.form.idCardBackImageId = image.id
          break
        case 'legalPersonHandheld':
          this.legalPersonHandheldImage = imageData
          this.form.legalPersonHandheldImageId = image.id
          break
        case 'otherFile':
          this.otherFiles.push({
            id: image.id,
            url: image.url || image.path,
            name: image.alias || image.filename || '资质文件',
            description: ''
          })
          break
      }
    },

    removeImage(type) {
      this.$confirm('确认删除该图片吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        switch (type) {
          case 'businessLicense':
            this.businessLicenseImage = null
            this.form.businessLicenseImageId = null
            break
          case 'foodLicense':
            this.foodLicenseImage = null
            this.form.foodLicenseImageId = null
            break
          case 'drugLicense':
            this.drugLicenseImage = null
            this.form.drugLicenseImageId = null
            break
          case 'idCardFront':
            this.idCardFrontImage = null
            this.form.idCardFrontImageId = null
            break
          case 'idCardBack':
            this.idCardBackImage = null
            this.form.idCardBackImageId = null
            break
          case 'legalPersonHandheld':
            this.legalPersonHandheldImage = null
            this.form.legalPersonHandheldImageId = null
            break
        }
        this.$message.success('图片已删除')
      }).catch(() => {})
    },

    openImageUploader(categoryName) {
      this.imageUploader.category = { name: categoryName }
      this.imageUploader.visible = true
    },

    handleImageUploadComplete(uploadedImage) {
      this.$message.success('图片上传成功，请在图片库中选择已上传的图片')
      if (uploadedImage && this.currentImageType) {
        this.setImageByType(this.currentImageType, uploadedImage)
      } else {
        setTimeout(() => {
          this.imageSelectorVisible = true
        }, 500)
      }
    },

    addOtherFile() {
      this.currentImageType = 'otherFile'
      this.imageSelectorVisible = true
    },

    removeOtherFile(index) {
      this.$confirm('确认删除该文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.otherFiles.splice(index, 1)
        this.$message.success('文件已删除')
      }).catch(() => {})
    },

    previewImage(image) {
      if (image.url) {
        this.previewDialog.url = image.url
        this.previewDialog.visible = true
      } else {
        this.$message.warning('图片链接不存在')
      }
    },

    copyImageUrl(url) {
      navigator.clipboard.writeText(url)
          .then(() => this.$message.success('链接已复制'))
          .catch(() => this.$message.error('复制失败'))
    },

    buildSectionData(section) {
      const baseData = {
        merchantId: this.storeId
      }

      if (this.savedIds[section]) {
        baseData.id = this.savedIds[section]
      }

      const toTimestamp = (dateStr) => {
        if (!dateStr) return null
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return null
        return date.getTime()
      }

      switch (section) {
        case 'businessLicense':
          return {
            ...baseData,
            businessLicenseNo: this.form.businessLicenseNo,
            companyName: this.form.companyName,
            legalPerson: this.form.legalPerson,
            registeredAddress: this.form.registeredAddress,
            businessLicenseImageId: this.form.businessLicenseImageId,
            businessScope: this.form.businessScope,
            establishDate: toTimestamp(this.form.establishDate),
            businessLicenseExpireDate: toTimestamp(this.form.businessLicenseExpireDate),
            isLongTerm: this.form.isLongTerm
          }
        case 'foodLicense':
          return {
            ...baseData,
            foodLicenseNo: this.form.foodLicenseNo,
            foodBusinessItems: this.form.foodBusinessItems,
            foodLicenseExpireDate: toTimestamp(this.form.foodLicenseExpireDate),
            foodLicenseImageId: this.form.foodLicenseImageId,
            foodLicenseIsLongTerm: this.form.foodLicenseIsLongTerm
          }
        case 'drugLicense':
          return {
            ...baseData,
            drugLicenseNo: this.form.drugLicenseNo,
            drugBusinessScope: this.form.drugBusinessScope,
            drugLicenseExpireDate: toTimestamp(this.form.drugLicenseExpireDate),
            drugLicenseImageId: this.form.drugLicenseImageId,
            drugLicenseIsLongTerm: this.form.drugLicenseIsLongTerm
          }
        case 'legalPerson':
          return {
            ...baseData,
            legalPersonName: this.form.legalPersonName,
            legalPersonIdCard: this.form.legalPersonIdCard,
            legalPersonPhone: this.form.legalPersonPhone,
            idCardFrontImageId: this.form.idCardFrontImageId,
            idCardBackImageId: this.form.idCardBackImageId
          }
        case 'legalPersonHandheld':
          return {
            ...baseData,
            legalPersonHandheldImageId: this.form.legalPersonHandheldImageId
          }
        case 'other':
          return {
            ...baseData,
            otherQualificationIds: this.otherFiles.length > 0 ? JSON.stringify(this.otherFiles.map(f => f.id)) : null,
            otherQualificationDesc: this.form.otherQualificationDesc,
            otherFilesDesc: JSON.stringify(this.otherFiles.map(f => ({ id: f.id, description: f.description })))
          }
        default:
          return {}
      }
    },

    async validateSection(section) {
      const requiredSections = {
        businessLicense: ['businessLicenseNo', 'companyName', 'legalPerson', 'registeredAddress', 'businessLicenseImageId'],
        legalPerson: ['legalPersonName', 'legalPersonIdCard', 'idCardFrontImageId', 'idCardBackImageId'],
        legalPersonHandheld: ['legalPersonHandheldImageId']
      }

      if (!requiredSections[section]) {
        return true
      }

      for (const field of requiredSections[section]) {
        const value = this.form[field]

        if (field === 'businessLicenseImageId' || field === 'idCardFrontImageId' ||
            field === 'idCardBackImageId' || field === 'legalPersonHandheldImageId') {
          if (value === null || value === undefined || value === 0) {
            return false
          }
        } else {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return false
          }
        }
      }
      return true
    },

    async saveSection(section) {
      if (!this.storeId) {
        this.$message.error('未获取到店铺信息，请刷新页面重试')
        return
      }

      const isValid = await this.validateSection(section)
      if (!isValid) {
        const errorMessages = {
          businessLicense: '请填写完整的营业执照信息',
          legalPerson: '请填写完整的法人信息',
          legalPersonHandheld: '请上传法人手持照片'
        }
        this.$message.error(errorMessages[section] || '请完善必填项后再保存')
        return
      }

      this.sectionSaving[section] = true
      try {
        const saveData = this.buildSectionData(section)
        const res = await api.merchant_saveQualification(saveData)
        const responseData = res.data || res

        if (responseData.code === '000') {
          if (responseData.data && responseData.data.id) {
            this.savedIds[section] = responseData.data.id
          }
          this.$message.success(this.savedIds[section] ? '更新成功' : '保存成功')
          await this.loadQualificationData()
        } else {
          this.$message.error(responseData.msg || '保存失败')
        }
      } catch (error) {
        console.error(`保存${section}失败:`, error)
        this.$message.error('保存失败，请重试')
      } finally {
        this.sectionSaving[section] = false
      }
    },

    async submitForAudit() {
      if (!this.storeId) {
        this.$message.error('未获取到店铺信息，请刷新页面重试')
        return
      }

      this.submitting = true
      const loadingInstance = this.$loading({
        lock: true,
        text: '正在保存并提交审核...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        const requiredSections = ['businessLicense', 'legalPerson', 'legalPersonHandheld']
        const optionalSections = ['foodLicense', 'drugLicense', 'other']

        // 验证必填区块
        for (const section of requiredSections) {
          const isValid = await this.validateSection(section)
          if (!isValid) {
            const sectionLabels = {
              businessLicense: '营业执照',
              legalPerson: '法人信息',
              legalPersonHandheld: '法人手持照片'
            }
            this.$message.error(`请先完善${sectionLabels[section]}信息`)
            loadingInstance.close()
            this.submitting = false
            return
          }
        }

        // 收集所有保存请求
        const savePromises = []

        for (const section of requiredSections) {
          const saveData = this.buildSectionData(section)
          savePromises.push(
              api.merchant_saveQualification(saveData).then(res => ({
                section,
                response: res.data || res
              }))
          )
        }

        for (const section of optionalSections) {
          const hasData = this.checkSectionHasData(section)
          if (hasData) {
            const saveData = this.buildSectionData(section)
            savePromises.push(
                api.merchant_saveQualification(saveData).then(res => ({
                  section,
                  response: res.data || res
                }))
            )
          }
        }

        // 并行执行所有保存请求
        const saveResults = await Promise.all(savePromises)

        let allSaved = true
        let failedSection = ''
        const sectionLabels = {
          businessLicense: '营业执照',
          foodLicense: '食品经营许可证',
          drugLicense: '药品经营许可证',
          legalPerson: '法人信息',
          legalPersonHandheld: '法人手持照片',
          other: '其他资质'
        }

        for (const result of saveResults) {
          if (result.response.code === '000') {
            if (result.response.data && result.response.data.id) {
              this.savedIds[result.section] = result.response.data.id
            }
          } else {
            allSaved = false
            failedSection = result.section
            this.$message.error(`${sectionLabels[result.section]}保存失败: ${result.response.msg || '未知错误'}`)
            break
          }
        }

        if (!allSaved) {
          loadingInstance.close()
          this.submitting = false
          return
        }

        // 提交审核
        const submitRes = await api.merchant_submitQualificationForAudit({
          merchantId: this.storeId,
          sectionIds: this.savedIds
        })
        const submitData = submitRes.data || submitRes

        if (submitData.code === '000') {
          this.$message.success('提交审核成功')
          this.form.status = 2
          await this.loadQualificationData()
        } else {
          this.$message.error(submitData.msg || '提交失败')
        }
      } catch (error) {
        console.error('提交审核失败:', error)
        this.$message.error('提交失败，请重试')
      } finally {
        loadingInstance.close()
        this.submitting = false
      }
    },

    checkSectionHasData(section) {
      switch (section) {
        case 'foodLicense':
          return !!(this.form.foodLicenseNo || this.form.foodBusinessItems || this.form.foodLicenseImageId)
        case 'drugLicense':
          return !!(this.form.drugLicenseNo || this.form.drugBusinessScope || this.form.drugLicenseImageId)
        case 'other':
          return !!(this.otherFiles.length > 0 || this.form.otherQualificationDesc)
        default:
          return false
      }
    },

    refreshData() {
      this.loading = true
      this.loadQualificationData().finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped lang="scss">
.qualification-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.qualification-header-card {
  margin-bottom: 20px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .header-left {
    flex: 1;

    h2 {
      margin: 0 0 8px 0;
      font-size: 24px;
      color: #303133;
    }

    .store-info {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;

      .store-name {
        font-size: 16px;
        color: #606266;
        font-weight: 500;
      }
    }

    .reject-reason {
      margin-top: 8px;
    }
  }

  .header-right {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
  }
}

.content-wrapper {
  display: flex;
  gap: 20px;
}

.sidebar-nav {
  width: 220px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 20px;
}

.nav-title {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.nav-list {
  padding: 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  border-radius: 6px;
  color: #606266;
  transition: all 0.3s;
  position: relative;

  &:hover {
    background-color: #f5f7fa;
    color: #409eff;
  }

  &.active {
    background-color: #ecf5ff;
    color: #409eff;
  }

  i {
    font-size: 16px;
  }

  span {
    font-size: 14px;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-left: auto;

    &.success {
      background-color: #67c23a;
    }

    &.warning {
      background-color: #e6a23c;
    }

    &.danger {
      background-color: #f56c6c;
    }
  }
}

.main-content {
  flex: 1;
  min-width: 0;
}

.section-card {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }

  ::v-deep .el-card__header {
    padding: 16px 20px;
    border-bottom: 1px solid #ebeef5;
    background: #fafafa;
  }

  ::v-deep .el-card__body {
    padding: 20px;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;

  i {
    font-size: 20px;
    color: #409eff;
  }
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 12px;

  .save-status {
    font-size: 13px;
    color: #67c23a;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      font-size: 16px;
    }
  }
}

.card-body {
  padding: 0;
}

.image-manager {
  .image-placeholder-container {
    margin-bottom: 12px;
  }

  .image-actions {
    margin-bottom: 12px;
    display: flex;
    gap: 12px;
  }

  .image-tips {
    color: #909399;
    font-size: 12px;
    line-height: 1.6;

    p {
      margin: 4px 0;
    }
  }
}

.other-files-manager {
  .file-list {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
  }

  .file-item {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    width: 200px;
    background: #fff;

    .file-info {
      margin-top: 8px;

      .file-name {
        font-size: 12px;
        color: #606266;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .file-desc-input {
        margin-top: 8px;
      }
    }
  }
}

@media (max-width: 768px) {
  .content-wrapper {
    flex-direction: column;
  }

  .sidebar-nav {
    width: 100%;
    position: static;
  }

  .nav-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .nav-item {
    flex: 1;
    min-width: 100px;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .qualification-header-card .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .header-right {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>